import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ADMIN_LIST_PAGE_SIZE,
  adminErrText,
  adminGet,
  adminSend,
  AdminApiError,
} from "../../api.ts";
import { AdminTable } from "../../components/AdminTable.tsx";

type FulfillmentStatusFilter = "pending" | "fulfilled" | "cancelled" | "auto";

interface FulfillmentItem {
  spinId: string;
  createdAt: string;
  userId: string;
  tgUsername: string | null;
  tgFirstName: string | null;
  prizeName: string;
  prizeType: string;
  isJackpot: boolean;
  fulfillmentStatus: FulfillmentStatusFilter;
  trackingNumber: string | null;
  fulfillmentNote: string | null;
  hasShippingAddress: boolean;
}

interface ShippingFull {
  spinId: string;
  confirmedAt: string;
  address: {
    recipientName: string;
    phone: string;
    country: string;
    state: string;
    city: string;
    postalCode: string;
    addressLine1: string;
    addressLine2: string | null;
    note: string | null;
  };
}

const STATUS_LABELS: Record<FulfillmentStatusFilter, string> = {
  pending: "待处理",
  fulfilled: "已完成",
  cancelled: "已取消",
  auto: "自动",
};

const STATUS_COLOR: Record<FulfillmentStatusFilter, string> = {
  pending: "orange",
  fulfilled: "green",
  cancelled: "red",
  auto: "gray",
};

export function FulfillmentTab(props: { readonly botId: string }) {
  const { botId } = props;
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<FulfillmentStatusFilter>("pending");
  const [cursor, setCursor] = useState<string | null>(null);
  const [fulfillOpen, setFulfillOpen] = useState<string | null>(null);
  const [shippingData, setShippingData] = useState<ShippingFull | null>(null);
  const [search, setSearch] = useState("");

  const fulfillForm = useForm<{ trackingNumber: string; note: string }>({
    initialValues: { trackingNumber: "", note: "" },
  });

  const list = useQuery({
    queryKey: ["bot", botId, "fulfillments", statusFilter, cursor],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("status", statusFilter);
      params.set("limit", String(ADMIN_LIST_PAGE_SIZE));
      if (cursor !== null) params.set("cursor", cursor);
      return adminGet<{ items: FulfillmentItem[]; nextCursor: string | null }>(
        `/bots/${botId}/fulfillments?${params.toString()}`,
      );
    },
    refetchInterval: 30_000,
  });

  const patchFulfillment = useMutation({
    mutationFn: (args: {
      spinId: string;
      status: "fulfilled" | "cancelled";
      trackingNumber?: string | null;
      note?: string | null;
    }) =>
      adminSend("PATCH", `/bots/${botId}/fulfillments/${args.spinId}`, {
        status: args.status,
        trackingNumber: args.trackingNumber ?? null,
        note: args.note ?? null,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已更新" });
      setFulfillOpen(null);
      fulfillForm.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "fulfillments"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const viewShipping = useMutation({
    mutationFn: (spinId: string) =>
      adminGet<ShippingFull>(`/bots/${botId}/spins/${spinId}/shipping-full`),
    onSuccess: (data) => setShippingData(data),
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const keyword = search.trim().toLowerCase();
  // 履约 API 仅 cursor 分页；搜索只作用于当前页已加载 items（运营可接受）
  const visibleItems = useMemo(() => {
    const items = list.data?.items ?? [];
    if (keyword === "") return items;
    return items.filter((item) => {
      const prize = item.prizeName.toLowerCase();
      const username = (item.tgUsername ?? "").toLowerCase();
      const firstName = (item.tgFirstName ?? "").toLowerCase();
      const userId = item.userId.toLowerCase();
      return (
        prize.includes(keyword) ||
        username.includes(keyword) ||
        firstName.includes(keyword) ||
        userId.includes(keyword)
      );
    });
  }, [list.data?.items, keyword]);

  const rows = visibleItems.map((item) => (
    <Table.Tr key={item.spinId}>
      <Table.Td style={{ whiteSpace: "nowrap" }}>
        {new Date(item.createdAt).toLocaleString("zh-CN")}
      </Table.Td>
      <Table.Td>
        {item.tgFirstName ?? item.tgUsername ?? item.userId.slice(0, 8)}
        {item.tgUsername !== null ? (
          <Text span size="xs" c="dimmed" ml={4}>
            @{item.tgUsername}
          </Text>
        ) : null}
      </Table.Td>
      <Table.Td>
        {item.prizeName}
        {item.isJackpot ? (
          <Badge size="xs" color="yellow" ml={4}>
            Jackpot
          </Badge>
        ) : null}
      </Table.Td>
      <Table.Td>
        <Badge size="xs" color="gray">
          {item.prizeType}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge size="xs" color={STATUS_COLOR[item.fulfillmentStatus]}>
          {STATUS_LABELS[item.fulfillmentStatus]}
        </Badge>
      </Table.Td>
      <Table.Td>{item.trackingNumber ?? "-"}</Table.Td>
      <Table.Td>
        <Text size="xs">{item.fulfillmentNote ?? "-"}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          {item.prizeType === "physical" && item.hasShippingAddress ? (
            <Button
              size="xs"
              variant="outline"
              loading={viewShipping.isPending}
              onClick={() => {
                modals.openConfirmModal({
                  title: "查看完整地址",
                  children: "查看该记录将留痕到 admin_audit 日志，确认继续？",
                  labels: { confirm: "确认查看", cancel: "取消" },
                  onConfirm: () => viewShipping.mutate(item.spinId),
                });
              }}
            >
              查看地址
            </Button>
          ) : null}
          {item.fulfillmentStatus === "pending" ? (
            <>
              <Button
                size="xs"
                color="green"
                variant="outline"
                onClick={() => {
                  fulfillForm.reset();
                  setFulfillOpen(item.spinId);
                }}
              >
                标记完成
              </Button>
              <Button
                size="xs"
                color="red"
                variant="outline"
                onClick={() => {
                  modals.openConfirmModal({
                    title: "确认取消",
                    children: "确定要取消该履约记录吗？此操作不可撤销。",
                    labels: { confirm: "取消履约", cancel: "返回" },
                    confirmProps: { color: "red" },
                    onConfirm: () =>
                      patchFulfillment.mutate({
                        spinId: item.spinId,
                        status: "cancelled",
                      }),
                  });
                }}
              >
                取消
              </Button>
            </>
          ) : null}
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
          <Text fw={600} size="lg">
            履约管理
          </Text>
          <Group gap="sm" wrap="wrap">
            <TextInput
              size="xs"
              placeholder="奖品名 / TG 用户（当前页）"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ width: 220 }}
            />
            <SegmentedControl
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v as FulfillmentStatusFilter);
                setCursor(null);
                setSearch("");
              }}
              data={[
                { label: "待处理", value: "pending" },
                { label: "已完成", value: "fulfilled" },
                { label: "已取消", value: "cancelled" },
                { label: "自动", value: "auto" },
              ]}
              size="xs"
            />
          </Group>
        </Group>
        {list.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} h={40} radius="sm" />
            ))}
          </Stack>
        ) : list.isError ? (
          <Text c="red" size="sm" ta="center" py="lg">
            {adminErrText((list.error as AdminApiError).code)}
          </Text>
        ) : (
          <AdminTable minWidth={800}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>中奖时间</Table.Th>
                <Table.Th>中奖者</Table.Th>
                <Table.Th>奖品</Table.Th>
                <Table.Th>类型</Table.Th>
                <Table.Th>状态</Table.Th>
                <Table.Th>发货单号</Table.Th>
                <Table.Th>备注</Table.Th>
                <Table.Th>操作</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Text c="dimmed" size="sm" ta="center">
                      暂无记录
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                rows
              )}
            </Table.Tbody>
          </AdminTable>
        )}
        <Group justify="flex-end" gap="sm" mt="sm">
          <Button
            variant="default"
            size="sm"
            disabled={cursor === null}
            onClick={() => setCursor(null)}
          >
            回到首页
          </Button>
          <Button
            variant="default"
            size="sm"
            disabled={list.data?.nextCursor === null || list.data?.nextCursor === undefined}
            onClick={() => {
              if (list.data?.nextCursor !== null && list.data?.nextCursor !== undefined) {
                setCursor(list.data.nextCursor);
              }
            }}
          >
            下一页
          </Button>
        </Group>
      </Card>

      <Modal
        title="标记完成"
        opened={fulfillOpen !== null}
        onClose={() => setFulfillOpen(null)}
        keepMounted={false}
      >
        <form
          onSubmit={fulfillForm.onSubmit((v) => {
            if (fulfillOpen === null) return;
            patchFulfillment.mutate({
              spinId: fulfillOpen,
              status: "fulfilled",
              trackingNumber: v.trackingNumber.trim() === "" ? null : v.trackingNumber.trim(),
              note: v.note.trim() === "" ? null : v.note.trim(),
            });
          })}
        >
          <Stack gap="sm">
            <TextInput
              label="发货单号"
              placeholder="可选，填写物流单号"
              maxLength={200}
              {...fulfillForm.getInputProps("trackingNumber")}
            />
            <Textarea
              label="备注"
              placeholder="可选，操作备注"
              maxLength={200}
              {...fulfillForm.getInputProps("note")}
            />
            <Button type="submit" loading={patchFulfillment.isPending} color="green">
              确认完成
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        title="完整收件地址（操作已留痕）"
        opened={shippingData !== null}
        onClose={() => setShippingData(null)}
        size="md"
      >
        {shippingData !== null ? (
          <Table>
            <Table.Tbody>
              <Table.Tr>
                <Table.Th>收件人</Table.Th>
                <Table.Td>{shippingData.address.recipientName}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>电话</Table.Th>
                <Table.Td>{shippingData.address.phone}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>国家</Table.Th>
                <Table.Td>{shippingData.address.country}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>邦 / 州</Table.Th>
                <Table.Td>{shippingData.address.state}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>城市</Table.Th>
                <Table.Td>{shippingData.address.city}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>PIN 码</Table.Th>
                <Table.Td>{shippingData.address.postalCode}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>详细地址 1</Table.Th>
                <Table.Td>{shippingData.address.addressLine1}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>详细地址 2</Table.Th>
                <Table.Td>{shippingData.address.addressLine2 ?? "-"}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>备注</Table.Th>
                <Table.Td>{shippingData.address.note ?? "-"}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>锁定时间</Table.Th>
                <Table.Td>{shippingData.confirmedAt}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        ) : null}
      </Modal>
    </>
  );
}
